import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import useApi from 'hooks/useApi';
import { PERMISSIONS, useHasPermission } from 'contexts/PermissionContext';

const HideFormFields = ({ teamUid, clientUID }: { teamUid: any, clientUID: any }) => {
  const hasHideFieldsPermission = useHasPermission(PERMISSIONS.ADD_EDIT_HIDE_UNHIDE_FIELDS);
  
  const { getFlowByTeamUid, getResourceTypes, createHidingField, updateHidingField, getAllHidingFields, deleteHidingField } = useApi();
  const [fieldOptions, setFieldOptions] = useState<any[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [hidingFields, setHidingFields] = useState<any[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [loadingOverview, setLoadingOverview] = useState(false);

  useEffect(() => {
    if (teamUid) {
      fetchAllHidingFields(teamUid);
      fetchFlowData();
    }
  }, [teamUid]);

  useEffect(() => {
    if (clientUID) {
      fetchRoles(clientUID);
    }
  }, [clientUID]);

  const fetchFlowData = async () => {
    if (!teamUid) return;
    try {
      const flowResp = await getFlowByTeamUid(teamUid);
      if (flowResp?.data?.steps) {
        setFieldOptions(flowResp.data.steps);
        console.log('getFlowByTeamUid response:', flowResp);
      }
    } catch (error) {
      console.error('Error fetching team flow data:', error);
    }
  };

  const fetchAllHidingFields = async (teamUid: string) => {
    setLoadingOverview(true);
    try {
      const response = await getAllHidingFields(teamUid);
      setHidingFields(response.data || []);
      console.log('getAllHidingFields response:', response.data);
    } catch (error) {
      console.error('Error fetching hiding fields:', error);
    }
    setLoadingOverview(false);
  };

  useEffect(() => {
    // Remove the direct fetchRoles call since we now get clientUID from flow data
  }, []);

  const fetchRoles = async (clientUID: string) => {
    try {
      const response = await getResourceTypes(clientUID, 'user');
      if (Array.isArray(response?.data)) {
        const processedRoles = response.data.map((role: any) => ({ 
          uid: role.uid, 
          id: role.uid, // Add id field for compatibility
          name: role.name,
          roleName: role.name // Add roleName field for compatibility
        }));
        setRolesList(processedRoles);
        console.log('Roles fetched:', processedRoles);
      }
    } catch (e) {
      console.error('Error fetching roles:', e);
    }
  };

  const formik = useFormik({
    initialValues: {
      fieldName: '',
      roleRestriction: '',
      completionCondition: ''
    },
    validationSchema: Yup.object({
      fieldName: Yup.array().min(1, 'At least one field is required').required('Field Name is required'),
      roleRestriction: Yup.array().min(1, 'At least one role is required').required('Role Restriction is required'),
      completionCondition: Yup.string().required('Completion Condition is required')
    }),
    onSubmit: async (values) => {
      console.log('Field Name:', values.fieldName);      
      let parsedFieldName = Array.isArray(values.fieldName)
        ? values.fieldName.map((item) => {
            try {
              return JSON.parse(item);
            } catch {
              return item;
            }
          })
        : [];

      // Parse roleRestriction (array of role uids)
      let parsedRoleRestriction = Array.isArray(values.roleRestriction) ? values.roleRestriction : [];

      // Parse completionCondition (stringified object)
      let parsedCompletionCondition = values.completionCondition;
      try {
        parsedCompletionCondition = JSON.parse(parsedCompletionCondition);
      } catch {
        // fallback to raw value
      }

      const submitValues = {
        fieldName: parsedFieldName,
        roleRestriction: parsedRoleRestriction,
        completionCondition: parsedCompletionCondition,
        teamUid
      };
      try {
        if (editingId) {
          // Update existing hiding field
          await updateHidingField(editingId, teamUid, submitValues);
          setEditingId(null);
          setSnackbarMsg('Field updated successfully');
        } else {
          // Create new hiding field
          await createHidingField(teamUid, submitValues);
          setSnackbarMsg('Field created successfully');
        }
        setSnackbarOpen(true);
        formik.resetForm();
        await fetchAllHidingFields(teamUid);
      } catch (err) {
        setSnackbarMsg('Error saving hiding field');
        setSnackbarOpen(true);
        console.error('Error saving hiding field:', err);
      }
    }
  });

  // Handler to populate form for editing
  const handleEdit = (row: any) => {
    setEditingId(row.id);
    formik.setValues({
      fieldName: Array.isArray(row.fieldName)
        ? row.fieldName.map((f: any) => JSON.stringify({ question: f.question, uid: f.uid, id: f.id, stepId: f.stepId }))
        : [],
      roleRestriction: Array.isArray(row.roleRestriction) ? row.roleRestriction : [],
      completionCondition: row.completionCondition
        ? JSON.stringify({ title: row.completionCondition.title, uid: row.completionCondition.uid, id: row.completionCondition.id })
        : ''
    });
  };

  // Handler to delete a hiding field
  const handleDelete = async (id: string) => {
    try {
      await deleteHidingField(id);
      setSnackbarMsg('Field deleted successfully');
      setSnackbarOpen(true);
      await fetchAllHidingFields(teamUid);
    } catch (err) {
      setSnackbarMsg('Error deleting hiding field');
      setSnackbarOpen(true);
      console.error('Error deleting hiding field:', err);
    }
  };

  return (
    <Box p={2}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarMsg.includes('Error') ? 'error' : 'success'} sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
      <Typography variant="h5" gutterBottom>
        Hide Form Fields
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <FormControl fullWidth size="small" error={Boolean(formik.touched.fieldName && formik.errors.fieldName)}>
            <InputLabel id="field-name-label">Field Name</InputLabel>
            <Select
              labelId="field-name-label"
              id="fieldName"
              name="fieldName"
              multiple
              value={
                Array.isArray(formik.values.fieldName) ? formik.values.fieldName : formik.values.fieldName ? [formik.values.fieldName] : []
              }
              label="Field Name"
              onChange={(e) => {
                formik.setFieldValue('fieldName', e.target.value);
              }}
              onBlur={formik.handleBlur}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) return '';
                return selected
                  .map((sel: string) => {
                    try {
                      const obj = JSON.parse(sel);
                      return obj.question || '';
                    } catch {
                      return sel;
                    }
                  })
                  .join(', ');
              }}
            >
              {Array.isArray(fieldOptions) &&
                fieldOptions.map((step, idx2) => {
                  const allFields = step.component?.fields || [];
                  if (allFields.length === 0) return null;
                  return [
                    <MenuItem key={step.title + idx2} disabled divider>
                      <strong>{step.title}</strong>
                    </MenuItem>,
                    ...allFields.map((field: any) => (
                      <MenuItem
                        key={field._id}
                        value={JSON.stringify({ question: field.question, uid: field.uid, id: field.id, stepId: step.id })} // Ensure unique value for each field
                      >
                        {field.question}
                      </MenuItem>
                    ))
                  ];
                })}
            </Select>
            {formik.touched.fieldName && formik.errors.fieldName && (
              <Typography color="error" variant="caption">
                {formik.errors.fieldName}
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth size="small" error={Boolean(formik.touched.roleRestriction && formik.errors.roleRestriction)}>
            <InputLabel id="role-restriction-label">Role Restriction</InputLabel>
            <Select
              labelId="role-restriction-label"
              id="roleRestriction"
              name="roleRestriction"
              multiple
              value={
                Array.isArray(formik.values.roleRestriction)
                  ? formik.values.roleRestriction
                  : formik.values.roleRestriction
                  ? [formik.values.roleRestriction]
                  : []
              }
              label="Role Restriction"
              onChange={(e) => {
                formik.setFieldValue('roleRestriction', e.target.value);
              }}
              onBlur={formik.handleBlur}
              renderValue={(selected) => {
                if (!selected || selected.length === 0) return '';
                return selected
                  .map((sel: string) => {
                    const found = rolesList.find((role: any) => role.uid === sel || role.id === sel);
                    return found ? found.roleName || found.name : sel;
                  })
                  .join(', ');
              }}
            >
              {Array.isArray(rolesList) && rolesList.length > 0 ? (
                rolesList.map((role: any) => (
                  <MenuItem key={role.uid || role.id} value={role.uid || role.id}>
                    {role.roleName || role.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No roles available
                </MenuItem>
              )}
            </Select>
            {formik.touched.roleRestriction && formik.errors.roleRestriction && (
              <Typography color="error" variant="caption">
                {formik.errors.roleRestriction}
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth size="small" error={Boolean(formik.touched.completionCondition && formik.errors.completionCondition)}>
            <InputLabel id="completion-condition-label">On Completion Condition</InputLabel>
            <Select
              labelId="completion-condition-label"
              id="completionCondition"
              name="completionCondition"
              value={formik.values.completionCondition}
              label="On Completion Condition"
              onChange={(e) => {
                formik.setFieldValue('completionCondition', e.target.value);
              }}
              onBlur={formik.handleBlur}
              renderValue={(selected) => {
                if (!selected) return '';
                try {
                  const obj = JSON.parse(selected);
                  return obj.title || '';
                } catch {
                  return selected;
                }
              }}
            >
              {Array.isArray(fieldOptions) &&
                fieldOptions.map((step, idx2) => (
                  <MenuItem key={step.title + idx2} value={JSON.stringify({ title: step.title, uid: step.uid, id: step.id })}>
                    {step.title}
                  </MenuItem>
                ))}
            </Select>
            {formik.touched.completionCondition && formik.errors.completionCondition && (
              <Typography color="error" variant="caption">
                {formik.errors.completionCondition}
              </Typography>
            )}
          </FormControl>
          <Box display="flex" justifyContent="flex-end">
            <Button 
              type="submit" 
              variant="contained" 
              sx={{ bgcolor: 'secondary.dark', width: '100px', minWidth: '50px' }}
              disabled={!hasHideFieldsPermission}
            >
              Save
            </Button>
          </Box>
        </Stack>
      </form>
      {/* Overview Table */}
      <Box mt={4}>
        <Typography variant="h6" gutterBottom>
          HideField Overview
        </Typography>
        <TableContainer component={Paper}>
          {loadingOverview ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Completion Condition</TableCell>
                  <TableCell>Field Name(s)</TableCell>
                  <TableCell>Role Restriction</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hidingFields.map((row: any) => (
                  <TableRow key={row.uid || row.id}>
                    <TableCell>{row.completionCondition?.title}</TableCell>
                    <TableCell>{Array.isArray(row.fieldName) ? row.fieldName.map((f: any) => f.question).join(', ') : ''}</TableCell>
                    <TableCell>
                      {Array.isArray(row.roleRestriction)
                        ? row.roleRestriction.map((r: string, idx: number) => {
                            // Try to find the role by id or uid
                            const found = rolesList.find((role: any) => role.id === r || role.uid === r);
                            return <Chip key={r + idx} label={found ? (found.roleName || found.name) : r} size="small" sx={{ mr: 0.5 }} />;
                          })
                        : ''}
                    </TableCell>
                    <TableCell>{row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleEdit(row)}
                        disabled={!hasHideFieldsPermission}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDelete(row.id)}
                        disabled={!hasHideFieldsPermission}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>
    </Box>
  );
};

export default HideFormFields;
